/** Agent creation: the token-first launch (creation v2) and the paid creation credits (docs/API.md "Agent creation"). */
import { CreationCredits, CreationPayment } from '../creation.js'
import { CreationLaunch, LaunchAgent, LaunchParams, LaunchTerms, MyRefunds, PatchBasics, TickerAvailability, TickerAvailabilityQuery } from '../creation-v2.js'
import { HandleParams } from './params.js'
import { defineRoute, type RouteDef } from './define.js'

export const creationRoutes = {
  // Creation v2 (docs/CREATION-V2.md): identity → launch (one ETH payment) → basics → interview in the agent's thread.
  getTickerAvailability: defineRoute({
    method: 'GET',
    path: '/agents/ticker-available',
    auth: 'public',
    summary: 'Whether a creation-v2 ticker (3-6 upper-case letters) is free across agent tokens and open launches, with a suggestion',
    query: TickerAvailabilityQuery,
    response: TickerAvailability,
  }),
  getLaunchTerms: defineRoute({
    method: 'GET',
    path: '/agents/launch-terms',
    auth: 'public',
    summary: 'What creating an agent charges now and whether it includes its token launch, from the platform (free and without a token while the platform cannot launch)',
    response: LaunchTerms,
  }),
  launchAgent: defineRoute({
    method: 'POST',
    path: '/agents/launch',
    auth: 'session',
    summary: 'Launch an agent: identity + the ETH creation fee (one payCreation tx) when the platform can launch its token, else free without one; the agent exists in setting_up, basics follow',
    body: LaunchAgent,
    status: 201,
    response: CreationLaunch,
  }),
  getLaunch: defineRoute({
    method: 'GET',
    path: '/agents/launch/:id',
    auth: 'session',
    summary: 'A launch of the caller: payment, agent, token, thread, basics and interview state',
    params: LaunchParams,
    response: CreationLaunch,
    owner: true,
  }),
  patchAgentBasics: defineRoute({
    method: 'PATCH',
    path: '/agents/:handle/basics',
    auth: 'session',
    summary: "Edit a launched agent's basics (owner); posts a basics message with the change into the agent's thread",
    params: HandleParams,
    body: PatchBasics,
    response: CreationLaunch,
    owner: true,
  }),
  createCreationPayment: defineRoute({
    method: 'POST',
    path: '/agents/creation',
    auth: 'session',
    summary: 'Pay for one agent creation: 1 USDG payment intent (kind agent_creation) + [approve?, pay] txs; the credit is recorded on confirmation',
    status: 201,
    response: CreationPayment,
  }),
  listMyRefunds: defineRoute({
    method: 'GET',
    path: '/me/refunds',
    auth: 'session',
    summary: 'Refunds of my creation fees charged for token launches the platform refused, with their state (filed with the platform, decided by its operator)',
    response: MyRefunds,
  }),
  listMyCreationCredits: defineRoute({
    method: 'GET',
    path: '/me/creation-credits',
    auth: 'session',
    summary: 'My agent creation credits (paid, consumed, refunded), newest first, with their latest interview',
    response: CreationCredits,
  }),
} as const satisfies Record<string, RouteDef>
